import React from 'react'

import { Stack, Typography } from '@mui/material'

import { FieldLabel } from '@repo/ui'
// Direct path, not the `components` barrel: the barrel re-exports both `input`
// (this file) and `display` (CountBadge), so going through it here risks a
// require cycle back to this same module.
import { CountBadge } from '../../display/CountBadge/CountBadge'

interface FilterFieldProps {
  /** Field title (matches the column headerName). */
  label?: React.ReactNode
  /** Show the inline "Clear" action (right of the title). */
  showClear?: boolean
  onClear?: () => void
  /** How many options are selected — rendered as a small count badge by the title. */
  count?: number
  children: React.ReactNode
}

/**
 * VERA-455 — the BASE filter field every drawer field inherits.
 *
 * Owns the title row (label + count badge + optional Clear), so all filter fields line up
 * identically instead of each hand-rolling their own (DateRange was `spacing={4}`, others
 * `spacing={2}`, MultiSelect added `mb`). The between-field gap is owned in ONE place too:
 * the Filters drawer's list `spacing`.
 *
 * The label→control gap is NOT owned here — it belongs to `@repo/ui`'s `FieldLabel`, which
 * every other field in the app already uses (working-conventions §31: one source for the
 * label treatment AND its gap, never faked per-component with a margin on the control).
 * This row previously hand-rolled its own `<Typography>` + `spacing={2}`, which is precisely
 * the drift §31 exists to prevent — the filter drawer's fields were the last set standing
 * outside it. The count badge rides in as the label's `adornment`, the Clear as its `action`.
 */
export const FilterField = ({ label, showClear = false, onClear, count = 0, children }: FilterFieldProps) => {
  return (
  <Stack>
    <FieldLabel
      label={label}
      // `body2` — the filter drawer's title size, larger than a form field's `body3`.
      variant="body2"
      // Count badge — how many options are selected in this facet. Extracted to
      // `CountBadge` (working-conventions §36) — the Ask panel's history bulk-select
      // bar needed the identical treatment for its "N selected" count.
      adornment={<CountBadge count={count} />}
      // Always rendered (hidden, not unmounted) so toggling a selection doesn't jump the layout.
      action={
        <Typography
          onClick={showClear ? onClear : undefined}
          sx={{
            textDecoration: 'underline',
            fontSize: 14,
            cursor: 'pointer',
            visibility: showClear ? 'visible' : 'hidden',
          }}
        >
          Clear
        </Typography>
      }
    />
    {children}
  </Stack>
  )
}
